import { action, computed, makeObservable, observable, reaction } from 'mobx';
import { Amplify, Auth, Hub } from 'aws-amplify';
import * as Sentry from '@sentry/browser';
import i18n, { t } from 'i18next';
import uniqBy from 'lodash/uniqBy';

import epaxios from '@/utils/epaxios';
import gtag from '@/utils/gtag';

import RootStore from '../RootStore';
import { PersistentStorage } from '../PersistentStorage';
import { getAmplifyConfig } from '@/store/User/amplifyConfig';
import { getResolvedBrandingFromStations } from './UserStore.utils';
import type { BrandNamesSet } from './UserStore.utils';
export type AuthenticationType = 'password' | 'sso';

const AUTHENTICATION_TYPE_KEY = 'userStore.authenticationType';
const SHOWCASE_ENABLED_KEY = 'showCaseEnabled';

export default class UserStore {
  rootStore: typeof RootStore;
  storage: PersistentStorage;
  @observable user;
  @observable stations;
  @observable isReady = false;
  @observable getUserMeRequestFailed = false;
  @observable hasUserLoggedIn = false;
  @observable hasSignedEula = false;
  @observable authenticationType: AuthenticationType;
  @observable samlErrorResponse: null | string = null;
  @observable ssoLoginFailureResponse: null | string = null;
  @observable passwordLoginFailureResponse: null | string = null;
  @observable showCaseEnabled = false;
  @observable whiteLabelingTerms: BrandNamesSet;

  constructor(rootStore) {
    makeObservable(this);
    this.storage = new PersistentStorage();
    this.rootStore = rootStore;
    this.loadShowCaseState();
    this.initReactions();
    this.initAuthentication();
  }

  private initReactions() {
    reaction(
      () => this.isAuthenticated,
      () => {
        if (!this.isAuthenticated) return;
        this.getMe();
      },
    );

    reaction(
      () => this.getAuthenticationType,
      () => {
        const authConfig = getAmplifyConfig(this.getAuthenticationType, import.meta.env.VITE_WEB_URL);
        Amplify.configure(authConfig);
      },
    );
  }

  private initAuthentication() {
    if (this.storage.has(AUTHENTICATION_TYPE_KEY)) {
      this.authenticationType = this.storage.get(AUTHENTICATION_TYPE_KEY);
      // since getCurrentUser() is redirecting the URL, we need to keep the saml error response if exists
      this.samlErrorResponse = new URLSearchParams(window.location.search).get('error_description');
      this.authenticateCurrentUser();
    } else {
      this.resetUser();
    }

    this.ssoLoginFailureResponse = localStorage.getItem('SSOLoginFailure');
    localStorage.removeItem('SSOLoginFailure');
    this.setUpAuthListener();
  }

  private setUpAuthListener() {
    Hub.listen('auth', async ({ payload }) => {
      const { event } = payload;
      if (event === 'signOut') {
        this.resetUser();
      }
    });
  }

  @action getMe = async () => {
    try {
      const { data } = await epaxios.get(`/user/me`);

      this.user = { ...this.user, ...data };
      this.stations = uniqBy(data.stations || [], 'id');
      this.whiteLabelingTerms = getResolvedBrandingFromStations(this.stations, data.regions);
      if (this.stations.length === 0) {
        localStorage.setItem('SSOLoginFailure', t('LoginFailureNoStations'));
        this.passwordLoginFailureResponse = t('LoginFailureNoStations');
        await this.signOut();
        return;
      }

      this.hasSignedEula = this.user.hasSignedLatestEula;
      this.isReady = true;
      this.hasUserLoggedIn = true;

      Sentry.configureScope(scope => {
        scope.setUser({
          id: data.id,
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          phoneNumber: data.phoneNumber,
        });
      });

      if (data.language) {
        localStorage.setItem('language', data.language);
      }
    } catch (e) {
      if (e.response?.status === 403) {
        localStorage.setItem('SSOLoginFailure', t('LoginFailureUnhautorizedSSO'));
        await this.signOut();
      } else {
        this.getUserMeRequestFailed = true;
      }
    }
  };

  @action updateStations = stations => {
    const mergedStations = stations.map((station, i) => ({ ...this.stations[i], ...station }));
    this.stations = uniqBy(mergedStations || [], 'id');
  };

  @action signIn = async (username, password) => {
    try {
      const user = await Auth.signIn(username?.trim(), password);
      switch (user.challengeName) {
        case 'SMS_MFA':
          return Promise.resolve(user);

        default:
          this.user = user;
          gtag.login();
          return Promise.resolve(user);
      }
    } catch (e) {
      this.resetUser();
      return Promise.reject(e);
    }
  };

  @action signOut = async () => {
    try {
      const { pidEditStore } = this.rootStore;
      pidEditStore.disableEditModeLock();
      // placeholder for edit mode unlock?
      await Auth.signOut();
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      this.resetUser();
    }
  };

  @action authenticateCurrentUser = async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      this.user = user;
      this.samlErrorResponse = null;
      this.ssoLoginFailureResponse = null;
      this.passwordLoginFailureResponse = null;
      return user;
    } catch (e) {
      this.resetUser();
    }
  };

  @action confirmSignIn = async ({ user, mfaCode, mfaType }) => {
    try {
      const loggedUser = await Auth.confirmSignIn(user, mfaCode, mfaType);
      this.user = loggedUser;
      gtag.login();
    } catch (e) {
      return Promise.reject(e);
    }
  };

  @action setLanguage = async (language: string) => {
    await i18n.changeLanguage(language);
    localStorage.setItem('language', language);
  };

  @action resetUser() {
    this.setShowCaseEnabled(false);
    this.user = undefined;
    this.stations = undefined;
    this.getUserMeRequestFailed = false;
    this.hasUserLoggedIn = false;
    this.hasSignedEula = false;
    this.isReady = true;
  }

  @action setGetUserMeRequestFailed = value => {
    this.getUserMeRequestFailed = value;
  };

  @action setUserLoggedIn = value => {
    this.hasUserLoggedIn = value;
  };

  @action setMeasurementSystem = value => {
    this.user.measurementSystem && (this.user.measurementSystem = value);
  };

  @action setEulaField = value => {
    this.hasSignedEula = value;
    this.user.hasSignedLatestEula = value;
  };

  @action setAuthenticationType = (value: AuthenticationType) => {
    this.authenticationType = value;
    this.storage.set(AUTHENTICATION_TYPE_KEY, value);
  };

  @action getRoleNameByStationId(stationId: string | number): string | undefined {
    if (!this.stations) return undefined;
    const station = this.stations.find(s => s.id === stationId);
    return station?.roleName;
  }

  @computed
  get isIrcoEmail(): boolean {
    if (!this.user?.email) return false;
    return /@([a-zA-Z0-9.-]*\.)?irco\.com$/.test(this.user.email);
  }

  @computed
  get isShowCaseEnabled(): boolean {
    return this.showCaseEnabled;
  }

  @action setShowCaseEnabled(enabled: boolean) {
    this.showCaseEnabled = enabled;
    if (enabled) {
      localStorage.setItem(SHOWCASE_ENABLED_KEY, JSON.stringify(enabled));
    } else {
      localStorage.removeItem(SHOWCASE_ENABLED_KEY);
    }
  }

  private loadShowCaseState() {
    const savedState = localStorage.getItem('showCaseEnabled');
    if (savedState) {
      this.showCaseEnabled = JSON.parse(savedState);
    }
  }

  @computed get getAuthenticationType(): AuthenticationType {
    return this.authenticationType;
  }

  @computed get isAuthenticated() {
    return !!this.user;
  }

  @computed get isAuthenticatedAndAccepted() {
    return !!this.user && this.isEulaAccepted;
  }

  @computed get isEulaAccepted() {
    return this.hasSignedEula;
  }

  @computed get isStoreReady() {
    return this.isReady;
  }
}
